import { useState } from 'react';
import { useMyOrganization } from '@/hooks/organization/use-my-organization';
import { useListGroups } from '@/hooks/organization/use-list-groups';
import { useAuthClient } from '@/lib/authClient';
import type { Group } from '@/types/group';
import { Plus, MoreHorizontal, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { MemberDisplay } from '@/components/members/member-display';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { StyledCard } from '@/components/ui/styled-card';
import {
  PageHeader,
  PageTitle,
  PageDescription,
  PageHeaderInfo,
  PageContent,
  PageAction,
} from '@/components/layout/page-header';
import { formatName } from '@/lib/name';
import { NewGroupDialog } from '@/pages/groups/components/new-group-dialog';
import { PendingChangesAlert } from '@/components/shared/pending-changes-alert';
import { ReviewGroupChangesDialog } from '@/pages/groups/components/review-group-changes-dialog';
import { GroupMenuOptions } from '@/components/groups/group-menu-options';
import { EditGroupMembersDialog } from '@/components/groups/edit-group-members-dialog';
import { EditGroupNameDialog } from '@/components/groups/edit-group-name-dialog';
import { usePendingGroupProposals } from '@/hooks/admin/group/use-pending-group-proposals';
import { useDeleteGroupProposal } from '@/hooks/admin/group/use-delete-group-proposal';
import {
  ReorderableList,
  SortableItem,
} from '@/components/shared/reorderable-list';
import { ReorderError } from '@/components/shared/reorder-error';
import { useOrganizationItemOrder } from '@/hooks/admin/organization/use-organization-item-order';
import { cn } from '@/lib/utils';

const getGroupId = (group: Group) => group.id;

// Helper function to format counts
function formatCount(count: number, singular: string, plural?: string) {
  const pluralForm = plural || `${singular}s`;
  return count === 0
    ? `0 ${pluralForm}`
    : count === 1
      ? `1 ${singular}`
      : `${count} ${pluralForm}`;
}

export function GroupsPage() {
  const [isNewGroupDialogOpen, setIsNewGroupDialogOpen] = useState(false);
  const [isEditGroupNameDialogOpen, setIsEditGroupNameDialogOpen] =
    useState(false);
  const [isEditGroupDialogOpen, setIsEditGroupDialogOpen] = useState(false);
  const [selectedGroupForName, setSelectedGroupForName] =
    useState<Group | null>(null);
  const [selectedGroupForMembers, setSelectedGroupForMembers] =
    useState<Group | null>(null);
  const [accordionValue, setAccordionValue] = useState<string[]>([]);
  const [isReviewChangesDialogOpen, setIsReviewChangesDialogOpen] =
    useState(false);

  const handleReviewChanges = () => {
    setIsReviewChangesDialogOpen(true);
  };

  const { organization } = useMyOrganization();
  const { user } = useAuthClient();
  const isAdmin = organization?.isAdmin ?? false;
  const { proposals, refresh: refreshProposals } = usePendingGroupProposals();
  const {
    groups,
    isLoading: isLoadingGroups,
    refresh: refreshGroups,
  } = useListGroups(organization?.id ?? null);
  const {
    orderedItems: orderedGroups,
    orderedIds: orderedGroupIds,
    reorder: reorderGroups,
    isReordering,
    reorderError,
  } = useOrganizationItemOrder({
    items: groups,
    itemType: 'groups',
    getItemId: getGroupId,
    enabled: isAdmin,
  });
  const { deleteGroup } = useDeleteGroupProposal();

  if (!user) {
    return null;
  }

  const pendingChangesCount = proposals.length;

  // Helper to check if a group has a pending DELETE proposal
  const hasPendingDeleteProposal = (groupId: string): boolean => {
    return proposals.some(
      (p) => p.proposal.groupId === groupId && p.proposal.action === 'DELETE'
    );
  };

  const handleEditGroupName = (group: Group) => {
    setSelectedGroupForName(group);
    setIsEditGroupNameDialogOpen(true);
  };

  const handleEditGroupMembers = (group: Group) => {
    setSelectedGroupForMembers(group);
    setIsEditGroupDialogOpen(true);
  };

  const handleDeleteGroup = async (group: Group) => {
    if (!organization?.id) return;

    try {
      await deleteGroup({ groupId: group.id });
      // Refresh the proposals list to show the new DELETE proposal
      refreshProposals();
    } catch (error) {
      console.error('Failed to delete group:', error);
    }
  };

  return (
    <div className="">
      <PageHeader>
        <PageHeaderInfo>
          <PageTitle>Groups</PageTitle>
          <PageDescription>
            Manage approval groups for transactions and settings
          </PageDescription>
        </PageHeaderInfo>
        <PageAction>
          <div className="flex flex-col items-end gap-4">
            {isAdmin && pendingChangesCount > 0 ? (
              <PendingChangesAlert
                pendingChangesCount={pendingChangesCount}
                onReviewChanges={handleReviewChanges}
              />
            ) : null}
            {isAdmin && (
              <Button onClick={() => setIsNewGroupDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-1" />
                New Group
              </Button>
            )}
          </div>
        </PageAction>
      </PageHeader>

      <PageContent>
        <ReorderError message={reorderError} />
        {isLoadingGroups ? (
          <div className="h-48 flex items-center justify-center text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading groups...
          </div>
        ) : orderedGroups.length === 0 ? (
          <div className="h-48 flex items-center justify-center text-slate-500">
            No groups yet. Create your first group to get started.
          </div>
        ) : (
          <div className="relative before:content-[''] before:absolute before:inset-0 before:rounded-xl before:border before:border-border before:pointer-events-none before:z-1 before:bg-zinc-100">
            {/* Column Headers */}
            <div
              className={cn(
                'relative z-2 pr-12 grid grid-cols-[1fr_1fr_2fr] [&>div]:px-4 [&>div]:py-3 [&>div:first-child]:pl-6 [&>div:last-child]:pr-6 [&>div]:text-xs [&>div]:font-medium [&>div]:text-muted-foreground',
                isAdmin && 'pl-10 [&>div:first-child]:pl-0'
              )}
            >
              <div>Group name</div>
              <div>Policies</div>
              <div>Members</div>
            </div>

            {/* Groups Accordion */}
            <Accordion
              type="multiple"
              value={accordionValue}
              onValueChange={setAccordionValue}
              className="space-y-0 z-2 relative border border-border bg-background rounded-xl overflow-hidden"
            >
              <ReorderableList
                ids={orderedGroupIds}
                disabled={!isAdmin || isReordering}
                onReorder={reorderGroups}
              >
                {orderedGroups.map((group) => (
                  <SortableItem
                    key={group.id}
                    id={group.id}
                    label={group.name}
                    disabled={!isAdmin || isReordering}
                  >
                    {({ setNodeRef, style, dragHandle }) => {
                      const accordionItem = (
                        <AccordionItem
                          ref={setNodeRef}
                          style={style}
                          value={group.id}
                          className="[&:not(:last-child)]:border-b border-zinc-100 bg-background"
                        >
                          <AccordionTrigger
                            className="hover:no-underline py-0 pl-0"
                            leading={
                              isAdmin ? (
                                <div className="flex w-10 shrink-0 items-center justify-center bg-background">
                                  {dragHandle}
                                </div>
                              ) : undefined
                            }
                          >
                            <div className="flex w-full justify-between items-center pr-4">
                              <div
                                className={cn(
                                  'grid grid-cols-[1fr_1fr_2fr] w-full text-left [&>div]:px-4 [&>div]:py-4 [&>div:first-child]:pl-6 [&>div:last-child]:pr-6',
                                  isAdmin && '[&>div:first-child]:pl-0'
                                )}
                              >
                                <div className="font-semibold text-base">
                                  {group.name}
                                </div>
                                <div className="text-sm text-muted-foreground">
                                  {formatCount(
                                    group.policies.length,
                                    'policy',
                                    'policies'
                                  )}
                                </div>
                                <div className="flex flex-wrap gap-1">
                                  {group.members.slice(0, 3).map((member) => (
                                    <MemberDisplay
                                      key={member.id}
                                      member={{
                                        ...member,
                                        name: formatName(member.name),
                                      }}
                                      size="sm"
                                      variant="badge"
                                    />
                                  ))}
                                  {group.members.length > 3 && (
                                    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-muted text-xs text-muted-foreground">
                                      +{group.members.length - 3}
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Kebab Menu */}
                              {organization?.isAdmin && (
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-8 w-8 p-0 data-[state=open]:bg-muted"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <MoreHorizontal className="h-4 w-4" />
                                      <span className="sr-only">Open menu</span>
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent
                                    align="end"
                                    className="w-48 z-[9999]"
                                  >
                                    <GroupMenuOptions
                                      group={group}
                                      onEditName={handleEditGroupName}
                                      onEditMembers={handleEditGroupMembers}
                                      onDeleteGroup={handleDeleteGroup}
                                      onActionComplete={() => {}}
                                      disableDelete={hasPendingDeleteProposal(
                                        group.id
                                      )}
                                      MenuItem={DropdownMenuItem}
                                      MenuSeparator={DropdownMenuSeparator}
                                    />
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              )}
                            </div>
                          </AccordionTrigger>
                          <AccordionContent className="bg-muted border-t border-border">
                            <div className="px-6 py-4">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Policies Section */}
                                <StyledCard title="Policies">
                                  {group.policies.length > 0 ? (
                                    <ul className="space-y-1">
                                      {group.policies.map((policy) => (
                                        <li
                                          key={policy.id}
                                          className="text-sm p-2 rounded-md bg-accent/10 flex items-center justify-between gap-2"
                                        >
                                          <span>{policy.name}</span>
                                          <div className="flex flex-wrap justify-end gap-1">
                                            {policy.roles.includes(
                                              'initiator'
                                            ) && (
                                              <Badge
                                                variant="outline"
                                                className="text-xs font-normal"
                                              >
                                                Initiator
                                              </Badge>
                                            )}
                                            {policy.roles.includes(
                                              'approver'
                                            ) && (
                                              <Badge
                                                variant="outline"
                                                className="text-xs font-normal"
                                              >
                                                Approver
                                              </Badge>
                                            )}
                                          </div>
                                        </li>
                                      ))}
                                    </ul>
                                  ) : (
                                    <p className="text-sm text-muted-foreground">
                                      No policies associated.
                                    </p>
                                  )}
                                </StyledCard>

                                {/* Members Section */}
                                <StyledCard title="Members">
                                  {group.members.length > 0 ? (
                                    <ul className="space-y-1">
                                      {group.members.map((member) => (
                                        <li
                                          key={member.id}
                                          className="text-sm p-2 rounded-md bg-accent/10"
                                        >
                                          {formatName(member.name)}
                                        </li>
                                      ))}
                                    </ul>
                                  ) : (
                                    <p className="text-sm text-muted-foreground">
                                      No members associated.
                                    </p>
                                  )}
                                </StyledCard>
                              </div>
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      );

                      return isAdmin ? (
                        <ContextMenu>
                          <ContextMenuTrigger asChild>
                            {accordionItem}
                          </ContextMenuTrigger>
                          <ContextMenuContent className="w-48 z-[9999]">
                            <GroupMenuOptions
                              group={group}
                              onEditName={handleEditGroupName}
                              onEditMembers={handleEditGroupMembers}
                              onDeleteGroup={handleDeleteGroup}
                              onActionComplete={() => {}}
                              disableDelete={hasPendingDeleteProposal(group.id)}
                              MenuItem={ContextMenuItem}
                              MenuSeparator={ContextMenuSeparator}
                            />
                          </ContextMenuContent>
                        </ContextMenu>
                      ) : (
                        accordionItem
                      );
                    }}
                  </SortableItem>
                ))}
              </ReorderableList>
            </Accordion>
          </div>
        )}
      </PageContent>

      <NewGroupDialog
        open={isNewGroupDialogOpen}
        onOpenChange={setIsNewGroupDialogOpen}
        onGroupCreated={() => {
          refreshProposals();
          setIsNewGroupDialogOpen(false);
        }}
      />

      <EditGroupNameDialog
        open={isEditGroupNameDialogOpen}
        onOpenChange={setIsEditGroupNameDialogOpen}
        group={selectedGroupForName}
        onGroupUpdated={() => {
          refreshGroups();
          setIsEditGroupNameDialogOpen(false);
        }}
      />

      <EditGroupMembersDialog
        open={isEditGroupDialogOpen}
        onOpenChange={setIsEditGroupDialogOpen}
        group={selectedGroupForMembers}
        onGroupUpdated={() => {
          refreshProposals();
          refreshGroups();
          setIsEditGroupDialogOpen(false);
        }}
      />

      {isAdmin ? (
        <ReviewGroupChangesDialog
          open={isReviewChangesDialogOpen}
          onOpenChange={setIsReviewChangesDialogOpen}
          proposals={proposals}
          onApplied={() => {
            refreshProposals();
            refreshGroups();
          }}
          refreshProposals={refreshProposals}
        />
      ) : null}
    </div>
  );
}
